import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
    FileSpreadsheet,
    FileText,
    Users,
    Receipt,
    Download,
    Loader2,
    Info,
    Wallet,
    Percent,
    Landmark,
    BookOpen,
    FileCode,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PageHeader } from '../components/PageHeader';
import { MonthPicker, currentMonth, labelForMonth, useSharedMonth } from "../components/MonthPicker";
import { api, fmtMoney } from '../lib/api';

/**
 * CA Export page (Phase 10) — lets the workspace owner package everything
 * their Chartered Accountant needs for a given month:
 *   - Ledger ZIP (purchases, line items, vendor payments, staff — CSVs)
 *   - GST Summary PDF (vendor-wise summary + bill-by-bill listing)
 *   - Vendor Statements PDF (section per vendor with running balance)
 */
export default function CAExportPage() {
    const [month, setMonth] = useSharedMonth();
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(true);
    const [downloading, setDownloading] = useState({ ledger: false, gst: false, vendors: false, excel: false, tally: false });

    const loadPreview = useCallback(async (m) => {
        setPreviewLoading(true);
        try {
            const data = await api.caExportPreview(m);
            setPreview(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load month preview');
            setPreview(null);
        } finally {
            setPreviewLoading(false);
        }
    }, []);

    useEffect(() => {
        // MonthPicker can emit "all" — CA Export requires a specific month
        if (!month || month === 'all') return;
        loadPreview(month);
    }, [month, loadPreview]);

    const runDownload = async (key, fn, label) => {
        if (!month || month === 'all') {
            toast.error('Please pick a specific month first.');
            return;
        }
        setDownloading((s) => ({ ...s, [key]: true }));
        try {
            const res = await fn(month);
            toast.success(`Downloaded ${label}`, {
                description: res?.filename || undefined,
            });
        } catch (e) {
            // Blob response — decode possible JSON error body
            let detail = e?.response?.data?.detail || e?.message;
            if (e?.response?.data instanceof Blob) {
                try {
                    const txt = await e.response.data.text();
                    detail = JSON.parse(txt)?.detail || txt;
                } catch {
                    /* ignore */
                }
            }
            toast.error(`Failed to download ${label}`, { description: detail });
        } finally {
            setDownloading((s) => ({ ...s, [key]: false }));
        }
    };

    const monthLabel = labelForMonth(month);
    const isValidMonth = month && month !== 'all';
    const empty = preview && preview.bills_count === 0 && preview.payments_count === 0
        && preview.staff_transactions_count === 0 && preview.staff_months_count === 0;

    return (
        <div className="space-y-6" data-testid="ca-export-page">
            <PageHeader
                title="Export"
                subtitle="Package everything for a given month — ledger CSVs, a GST summary PDF, vendor-wise statements, an Excel workbook and a Tally XML — all filtered to the selected month."
                actions={
                    <MonthPicker
                        value={month}
                        onChange={setMonth}
                        includeAllTime={false}
                        testid="ca-export-month-picker"
                    />
                }
            />

            {!isValidMonth && (
                <Alert data-testid="ca-export-need-month">
                    <Info className="h-4 w-4" />
                    <AlertTitle>Select a month</AlertTitle>
                    <AlertDescription>
                        Pick a specific month above. Exports are always scoped to one calendar month.
                    </AlertDescription>
                </Alert>
            )}

            {/* Preview / summary card */}
            <Card className="p-5" data-testid="ca-export-preview-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                            Period
                        </div>
                        <div className="font-heading text-xl mt-0.5" data-testid="ca-export-month-label">
                            {monthLabel}
                        </div>
                    </div>
                    {previewLoading ? (
                        <Skeleton className="h-16 w-72" />
                    ) : preview ? (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-right">
                            <SummaryStat label="Bills" value={preview.bills_count} testid="ca-stat-bills" />
                            <SummaryStat label="Vendors" value={preview.vendors_count} testid="ca-stat-vendors" />
                            <SummaryStat label="Payments" value={preview.payments_count} testid="ca-stat-payments" />
                            <SummaryStat label="Staff Txns" value={preview.staff_transactions_count} testid="ca-stat-staff-txns" />
                        </div>
                    ) : null}
                </div>

                {previewLoading ? (
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Skeleton className="h-20" />
                        <Skeleton className="h-20" />
                        <Skeleton className="h-20" />
                        <Skeleton className="h-20" />
                    </div>
                ) : preview ? (
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-4">
                        <MoneyTile
                            icon={<Receipt size={16} className="text-muted-foreground" />}
                            label="Total Purchases"
                            value={fmtMoney(preview.totals.total)}
                            testid="ca-money-purchases"
                        />
                        <MoneyTile
                            icon={<Percent size={16} className="text-muted-foreground" />}
                            label="Total GST"
                            value={fmtMoney(preview.total_gst)}
                            hint={
                                preview.total_gst === 0 && preview.bills_count > 0
                                    ? 'GST fields not yet extracted on these bills.'
                                    : undefined
                            }
                            testid="ca-money-gst"
                        />
                        <MoneyTile
                            icon={<Landmark size={16} className="text-muted-foreground" />}
                            label="Taxable Value"
                            value={fmtMoney(preview.totals.taxable)}
                            testid="ca-money-taxable"
                        />
                        <MoneyTile
                            icon={<Wallet size={16} className="text-muted-foreground" />}
                            label="Payments Made"
                            value={fmtMoney(preview.payments_total)}
                            testid="ca-money-payments"
                        />
                    </div>
                ) : null}

                {empty && (
                    <div className="mt-4 text-sm text-muted-foreground" data-testid="ca-empty-note">
                        No bills, payments, or staff activity recorded for {monthLabel}. Downloads will still
                        produce valid (mostly empty) files if you need to send a "nil" report.
                    </div>
                )}
            </Card>

            {/* Download cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <DownloadCard
                    icon={<BookOpen size={22} className="text-primary" />}
                    title="Excel Workbook"
                    description="Multi-sheet .xlsx with Summary, Bills, Line Items, Expenses, Vendor Payments and Staff. Pre-formatted with ₹ currency and totals — CAs open it and start filing immediately."
                    bulletPoints={[
                        'Summary sheet with month totals',
                        'Bills + Line items with HSN + GST',
                        'Expenses, Vendor Payments, Staff',
                    ]}
                    testid="ca-card-excel"
                    button={
                        <Button
                            data-testid="ca-btn-download-excel"
                            disabled={!isValidMonth || downloading.excel}
                            onClick={() => runDownload('excel', api.caExportExcel, 'Excel Workbook')}
                            className="w-full gap-2"
                        >
                            {downloading.excel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={16} />}
                            {downloading.excel ? 'Preparing…' : 'Download .xlsx'}
                        </Button>
                    }
                />

                <DownloadCard
                    icon={<FileCode size={22} className="text-primary" />}
                    title="Tally Prime XML"
                    description="One-click Tally import. Contains a Purchase voucher per bill with CGST/SGST/IGST breakup. Open in Tally: Gateway → Import Data → Vouchers."
                    bulletPoints={[
                        'Purchase vouchers with GST split',
                        'Party ledger + input tax ledgers',
                        'Uses your workspace name as company',
                    ]}
                    testid="ca-card-tally"
                    button={
                        <Button
                            data-testid="ca-btn-download-tally"
                            disabled={!isValidMonth || downloading.tally}
                            onClick={() => runDownload('tally', api.caExportTally, 'Tally XML')}
                            className="w-full gap-2"
                        >
                            {downloading.tally ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={16} />}
                            {downloading.tally ? 'Preparing…' : 'Download .xml'}
                        </Button>
                    }
                />

                <DownloadCard
                    icon={<FileText size={22} className="text-primary" />}
                    title="GST Summary PDF"
                    description="Multi-page PDF: vendor-wise GST summary on page 1 (with grand totals), followed by a bill-by-bill listing."
                    bulletPoints={[
                        'Vendor-wise CGST / SGST / IGST totals',
                        'Grand totals card',
                        'Bill-by-bill listing (date, invoice #, taxes, total)',
                    ]}
                    testid="ca-card-gst"
                    button={
                        <Button
                            data-testid="ca-btn-download-gst"
                            disabled={!isValidMonth || downloading.gst}
                            onClick={() => runDownload('gst', api.caExportGstSummary, 'GST Summary PDF')}
                            className="w-full gap-2"
                        >
                            {downloading.gst ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={16} />}
                            {downloading.gst ? 'Preparing…' : 'Download PDF'}
                        </Button>
                    }
                />

                <DownloadCard
                    icon={<Users size={22} className="text-primary" />}
                    title="Vendor Statements PDF"
                    description="A single combined PDF with one section per vendor: bills + payments + running balance."
                    bulletPoints={[
                        'One section per vendor',
                        'Bills + payments with subtotals',
                        'Outstanding balance highlighted',
                    ]}
                    testid="ca-card-vendors"
                    button={
                        <Button
                            data-testid="ca-btn-download-vendors"
                            disabled={!isValidMonth || downloading.vendors}
                            onClick={() => runDownload('vendors', api.caExportVendorStatements, 'Vendor Statements PDF')}
                            className="w-full gap-2"
                        >
                            {downloading.vendors ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={16} />}
                            {downloading.vendors ? 'Preparing…' : 'Download PDF'}
                        </Button>
                    }
                />
            </div>

            {/* Footnote for CA */}
            <Card className="p-5 bg-muted/40">
                <div className="flex gap-3">
                    <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div className="text-sm text-muted-foreground space-y-1.5">
                        <div>
                            <span className="font-medium text-foreground">Tip:</span> Send all three files to your CA
                            together. The ledger CSVs are for data crunching, the GST summary is for the return, and the
                            vendor statements help reconcile individual vendor accounts.
                        </div>
                        <div>
                            <span className="font-medium text-foreground">About GST fields:</span> Ease My Bill extracts
                            GSTIN, HSN, CGST/SGST/IGST from your bills going forward. Bills uploaded before this feature
                            was enabled won't have GST columns populated — re-scan them from the Bills page if needed.
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}

function SummaryStat({ label, value, testid }) {
    return (
        <div className="min-w-[80px]">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="font-mono text-lg tabular-nums mt-0.5" data-testid={testid}>
                {value ?? 0}
            </div>
        </div>
    );
}

function MoneyTile({ icon, label, value, hint, testid }) {
    return (
        <div className="rounded-lg border border-border/60 bg-background/60 p-3">
            <div className="flex items-center justify-between gap-2">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
                {icon}
            </div>
            <div className="font-mono text-lg tabular-nums mt-1" data-testid={testid}>{value}</div>
            {hint && <div className="text-[10px] text-muted-foreground mt-1 leading-tight">{hint}</div>}
        </div>
    );
}

function DownloadCard({ icon, title, description, bulletPoints, button, testid }) {
    return (
        <Card className="p-5 flex flex-col gap-3" data-testid={testid}>
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                    {icon}
                </div>
                <div className="font-heading text-lg">{title}</div>
            </div>
            <div className="text-sm text-muted-foreground">{description}</div>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                {bulletPoints.map((b) => (
                    <li key={b}>{b}</li>
                ))}
            </ul>
            <div className="mt-auto pt-3">{button}</div>
        </Card>
    );
}
